'use client';

import { cartService, ClientProviders as CommonClientProviders, createClientLogger, CustomerAuthHandler } from '@order-up/client/vite';
import React, { useCallback } from 'react';
import { AnalyticsIdentity } from './components/AnalyticsIdentity';
import { CheckoutLoadingFallback } from './components/checkout-loading-fallback';

// Logger for checkout providers
const logger = createClientLogger('CheckoutAuthHandler');

/**
 * Checkout app providers — Firebase, React Query, theme. Every route.
 *
 * Unlike internal apps (management, admin), checkout is a PUBLIC/CUSTOMER app.
 * Customers don't belong to tenants, so we don't need SelectionProvider or AuthenticatedRoute.
 * The customer's cross-app session is {@link CustomerSession}, applied per route.
 */
export function ClientProviders({ children }: { children: React.ReactNode }) {
    return (
        <CommonClientProviders
            defaultTheme="light"
            themeStorageKey="bite-checkout-theme"
            enableSystem={false}
            loadingComponent={<CheckoutLoadingFallback />}
        >
            {children}
        </CommonClientProviders>
    );
}

/**
 * The customer's session: redeems the one-time `#handoff=` code the menu page
 * hands over for a Firebase sign-in as the same guest (auth continuity across
 * TLDs; a legacy `?session=` ID token is still exchanged) and gates every
 * customer page on it.
 *
 * Never around the POS keyed-entry page — that page is not part of this app
 * at all (it is served by the lean `embedded.html` entry; see src/embedded.tsx).
 * A WebView cannot complete this sign-in: production Firebase Auth enforces App
 * Check and a WebView fails reCAPTCHA attestation (measured 2026-09-11: App
 * Check DENY/INVALID on identitytoolkit at each attempt).
 */
export function CustomerSession({ children }: { children: React.ReactNode }) {
    return (
        <CheckoutAuthHandler>
            {/* Bridges live auth state → analytics identify/reset. Mounted
                inside both the AnalyticsProvider subtree (so useAnalytics
                resolves) and the auth subscription (so useAuth is live). */}
            <AnalyticsIdentity />
            {children}
        </CheckoutAuthHandler>
    );
}

/**
 * Checkout-specific auth handler with cart ownership validation
 */
function CheckoutAuthHandler({ children }: { children: React.ReactNode }) {
    /**
     * Validate if current session should be replaced based on cart ownership
     * Returns true to sign out, false to keep session
     */
    const validateCartOwnership = useCallback(async (currentUid: string, _sessionToken: string): Promise<boolean> => {
        try {
            // Use vanilla URLSearchParams (not React Router) since we're outside Router context
            if (typeof window === 'undefined') return true;

            const urlParams = new URLSearchParams(window.location.search);
            const cartGuid = urlParams.get('cart');

            if (!cartGuid) {
                logger.debug('No cart in URL, proceeding with token exchange');
                return true; // No cart to validate, proceed with sign-out
            }

            logger.debug('Loading cart owner to validate ownership', { cartGuid });

            // C9: resolve ONLY the cart owner UID via the authenticated API
            // (server-side collection-group lookup). The prior client-side
            // collectionGroup('carts') LIST is gone — anonymous sessions can no // scoped-path-ok: prose
            // longer enumerate carts.
            const owner = await cartService.getCartOwnerByGuid(cartGuid);

            if (!owner.found) {
                logger.warn('Cart not found', { cartGuid });
                return true; // Cart doesn't exist, proceed with token exchange
            }

            const cartOwnerUid = owner.customerId;

            if (!cartOwnerUid) {
                logger.warn('Cart has no customerId', { cartGuid });
                return true; // Anonymous cart, proceed with token exchange
            }

            // Compare current user UID with cart owner UID
            const ownershipMatch = currentUid === cartOwnerUid;

            logger.debug('Cart ownership validation', {
                cartGuid,
                currentUid,
                cartOwnerUid,
                match: ownershipMatch
            });

            // Return true if UIDs DON'T match (need to sign out)
            // Return false if UIDs DO match (keep session)
            return !ownershipMatch;

        } catch (error) {
            logger.error('Error validating cart ownership', error);
            // On error, proceed with token exchange to be safe
            return true;
        }
    }, []); // No dependencies - reads from window.location directly

    return (
        <CustomerAuthHandler
            sessionParamName="session"
            shouldSignOut={validateCartOwnership}
        >
            {children}
        </CustomerAuthHandler>
    );
}

